"use client";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  FilterOutlined,
  StarFilled,
  StarOutlined,
} from "@ant-design/icons";

import { Button, Checkbox, Popover, Rate, Space, theme, Typography } from "antd/lib";
import type { CheckboxValueType } from "antd/es/checkbox/Group";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTimezoneContext } from "@/context/TimezoneContext";
import { useTranslations } from "next-intl";
import useAxiosQuery from "@/hooks/useMyQuery";
import type { MyColumnType } from "@/app/components/MyTable";
import MyTable from "@/app/components/MyTable";
import { DateTimeFormat } from "@/constant";
import Heading from "@/app/components/Heading";
import MyCard from "@/app/components/MyCard";

const limit = 10;

interface EmergingIF {
  time_frame: string;
  candle_name: string;
  candle_time: string;
  candle_ago: string;
  star: string;
  confirm: number;
}

const timeFrameOptions = ["15M", "30M", "1H", "4H", "6H", "12H", "1D"];
const timeFrameInitOptions = ["1H", "4H", "6H", "12H", "1D"];
const typeOptions = ["Bullish", "Bearish"];
const reliabilityOptions = [
  {
    value: 1,
    label: (
      <Rate
        character={({ index }) =>
          (index || 0) + 1 <= 1 ? <StarFilled style={{ color: "rgba(0, 0, 0, 0.45)" }} /> : <StarOutlined />
        }
        disabled
        count={3}
        defaultValue={1}
      />
    ),
  },
  {
    value: 2,
    label: (
      <Rate
        character={({ index }) =>
          (index || 0) + 1 <= 2 ? <StarFilled style={{ color: "rgba(0, 0, 0, 0.45)" }} /> : <StarOutlined />
        }
        disabled
        count={3}
        defaultValue={2}
      />
    ),
  },
  {
    value: 3,
    label: (
      <Rate
        character={({ index }) =>
          (index || 0) + 1 <= 3 ? <StarFilled style={{ color: "rgba(0, 0, 0, 0.45)" }} /> : <StarOutlined />
        }
        disabled
        count={3}
        defaultValue={3}
      />
    ),
  },
];

export default function CandlestickPatterns({ symbol }: { symbol: string }) {
  const t = useTranslations("products");
  const { timezone } = useTimezoneContext();
  const { token: antdToken } = theme.useToken();

  const [total, setTotal] = useState<number>(0);
  const [tempFilterCustomize, setTempFilterCustomize] = useState<{ [key: string]: CheckboxValueType[] }>({
    timeFrame: timeFrameInitOptions,
    type: typeOptions,
    reliability: [2, 3],
  });
  const [timeFrame, setTimeFrame] = useState<CheckboxValueType[]>(timeFrameInitOptions);
  const [type, setType] = useState<CheckboxValueType[]>(typeOptions);
  const [reliability, setReliability] = useState<CheckboxValueType[]>([2, 3]);
  const [page, setPage] = useState<number>(1);
  const [openPopover, setOpenPopover] = useState<boolean>(false);
  const [timestampCompleted, setTimestampCompleted] = useState<number>();

  const { data: responseCompleted, isLoading } = useAxiosQuery<{
    data: { total?: number; result?: EmergingIF[] };
  }>(
    {
      url: "/v1/product/signal/candlestick/completed",
      params: {
        symbol,
        page: page,
        limit,
        timeframe: timeFrame.join(","),
        type: type.join(","),
        reliability: reliability.join(","),
      },
    },
    {
      queryKey: ["/v1/product/signal/candlestick/completed", symbol, page, timestampCompleted],
    },
  );
  useEffect(() => {
    if (responseCompleted?.data?.total !== total) {
      setTotal(responseCompleted?.data?.total ?? 0);
    }
  }, [responseCompleted?.data?.total, total]);
  const hidePopover = useCallback(() => {
    setTempFilterCustomize({
      timeFrame,
      type,
      reliability,
    });
    setOpenPopover(false);
  }, [reliability, timeFrame, type]);

  const handleChangeCheckbox = useCallback((name: string, checked: CheckboxValueType[]) => {
    setTempFilterCustomize(last => ({ ...last, [name]: checked }));
  }, []);

  const filterCustomize = useCallback(() => {
    setType(tempFilterCustomize.type);
    setTimeFrame(tempFilterCustomize.timeFrame);
    setReliability(tempFilterCustomize.reliability);
    setTimestampCompleted(Date.now());
    setOpenPopover(false);
  }, [tempFilterCustomize.reliability, tempFilterCustomize.timeFrame, tempFilterCustomize.type]);

  const columns = useMemo<MyColumnType<EmergingIF>[]>(
    () => [
      {
        title: t("Pattern"),
        key: "candle_name",
        width: 130,
        dataIndex: "candle_name",
        className: "h-[44px] w-[300px] py-1",
        render: text => <Typography.Text strong>{text}</Typography.Text>,
      },
      {
        title: t("Timeframe"),
        key: "time_frame",
        width: 130,
        dataIndex: "time_frame",
        className: "h-[44px] w-[300px] py-1",
        render: text => <Typography.Text strong>{text}</Typography.Text>,
      },
      {
        title: t("Reliability"),
        key: "star",
        width: 130,
        dataIndex: "star",
        className: "h-[44px] w-[300px] py-1",
        render: text => {
          if (typeof text === "number" && text) {
            return (
              <Rate
                character={({ index }) =>
                  (index || 0) + 1 <= text ? <StarFilled style={{ color: antdToken.colorIcon }} /> : <StarOutlined />
                }
                disabled
                count={3}
                defaultValue={text}
              />
            );
          }
          if (text) return <Typography.Text strong>{text}</Typography.Text>;
        },
      },
      {
        title: t("Back_test"),
        key: "confirm",
        className: "h-[42.6px]",
        dataIndex: "confirm",
        width: 130,
        align: "left",
        render: (_, record) => (
          <Typography.Text type={record.confirm === 1 ? "success" : "danger"}>
            {record.confirm ? <CheckCircleOutlined /> : <CloseCircleOutlined />}{" "}
            {record.confirm ? t("Correct") : t("Missed")}
          </Typography.Text>
        ),
      },
      {
        title: t("Candles_Ago"),
        key: "candle_ago",
        width: 130,
        dataIndex: "candle_ago",
        className: "h-[44px] w-[300px] py-1",
        render: text => <Typography.Text type={text}>{text}</Typography.Text>,
      },
      {
        title: t("Candles_Time"),
        key: "candle_time",
        width: 130,
        dataIndex: "candle_time",
        className: "h-[44px] w-[300px] py-1",
        render: (_, record) => {
          const day = dayjs(record.candle_time).tz(timezone.utc);
          if (!record.candle_time) return "";
          return <Typography.Text title={record.candle_time}>{day.format(DateTimeFormat)}</Typography.Text>;
        },
      },
    ],
    [antdToken.colorIcon, t, timezone.utc],
  );

  return (
    <MyCard
      title={<Heading title={t("Candlestick_Patterns")} tooltip={t("Candlestick_Patterns_tooltip")} />}
      extra={
        <Popover
          placement={"bottomRight"}
          content={
            <Space direction={"vertical"} size={24}>
              {[
                {
                  title: t("Timeframe"),
                  values: tempFilterCustomize.timeFrame,
                  options: timeFrameOptions,
                  action: (checked: CheckboxValueType[]) => handleChangeCheckbox("timeFrame", checked),
                },
                {
                  title: t("Type"),
                  values: tempFilterCustomize.type,
                  options: typeOptions,
                  action: (checked: CheckboxValueType[]) => handleChangeCheckbox("type", checked),
                },
                {
                  title: t("Reliability"),
                  values: tempFilterCustomize.reliability,
                  options: reliabilityOptions,
                  className: "flex flex-col space-x-0",
                  action: (checked: CheckboxValueType[]) => handleChangeCheckbox("reliability", checked),
                },
              ].map((item, index) => (
                <div key={index} className={"flex"}>
                  <div className={"w-36"}>
                    <Typography.Text className={"font-medium"}>{item.title}</Typography.Text>
                  </div>
                  <Checkbox.Group
                    className={item.className}
                    value={item.values}
                    options={item.options}
                    onChange={item.action}
                  />
                </div>
              ))}
              <div className={"w-full text-end"}>
                <Space>
                  <Button type={"default"} onClick={hidePopover}>
                    {t("Cancel")}
                  </Button>
                  <Button type={"primary"} onClick={filterCustomize}>
                    {t("Ok")}
                  </Button>
                </Space>
              </div>
            </Space>
          }
          arrow={false}
          title={
            <div className={"flex justify-between items-center"}>
              <Typography.Title level={5} className={"mb-0"}>
                {t("Filters")}
              </Typography.Title>
              <Button icon={<CloseOutlined />} type={"text"} title={"Close"} onClick={hidePopover} />
            </div>
          }
          trigger="click"
          open={openPopover}
          onOpenChange={open => setOpenPopover(open)}
        >
          <Button type={"default"} icon={<FilterOutlined />}>
            {t("Filters")}
          </Button>
        </Popover>
      }
    >
      <Space direction={"vertical"} className={"w-full"}>
        <MyTable
          isLoading={isLoading}
          columns={columns}
          className="candlestick-signal-table"
          dataSource={responseCompleted?.data?.result || []}
          scroll={{ x: 850 }}
          pagination={{
            totalRecords: total,
            defaultPageSize: limit + 1,
            onPageChange: setPage,
          }}
        />
      </Space>
    </MyCard>
  );
}
